import React, { useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Label, Input } from '@shared/components';
import { useDebounce } from '@shared/hooks/useDebounce';
import {
    buildCollectionLeadSchema,
    type CollectionLeadFormData,
} from '../schemas/CollectionLeadInputSchema';

const DEBOUNCE_MS = 350;

interface CollectionLeadInputProps {
    /** Inclusive lower bound of the product's dealer-override window
     *  (`Product.collectionLeadBounds.min`). Guaranteed >= 0 by
     *  `parseCollectionLeadBounds` (productTransform.ts) before this
     *  component is ever rendered. */
    min: number;
    /** Inclusive upper bound (`Product.collectionLeadBounds.max`). NON-OPTIONAL,
     *  together with `min` — the render gate (whether the trio resolves at
     *  all) lives in `PaymentStep` (D-B3 of the plan), so this component
     *  never has to represent "no bounds". */
    max: number;
    /** The product's own configured lead (`Product.collectionLeadMonths`),
     *  named in the hint so a blank field reads as "this is the number
     *  currently in force", not as an unexplained empty box. */
    productDefault: number;
    /** The EFFECTIVE lead currently in force — controlled from the parent
     *  (`PaymentStep`'s in-progress `leadOverrideMonths` local state), so a
     *  quote or method change is reflected here without this component
     *  owning that lifecycle itself. */
    valueMonths?: number;
    /** Fired ~350ms after the user stops typing a value that passes
     *  validation. Fired with `undefined` when the field is cleared — that
     *  is how the agent returns to the product's default (D-B7). NEVER
     *  fired for an invalid value — the caller's live preview params are
     *  only ever updated with a value that has already passed the
     *  client-side contract rules. */
    onDebouncedChange: (months: number | undefined) => void;
    debounceMs?: number;
}

/**
 * Agent-facing override of the collection lead for the item-based
 * scheduling family (#728, Slice B). Structurally a SIBLING of
 * `DepositInput` (D-B3 of the plan) — react-hook-form + `zodResolver`, a
 * `useMemo`'d per-render schema built from runtime bounds, `values:` (not
 * `defaultValues:`) so the parent controls the field, `mode: 'onChange'`, a
 * 350ms `useDebounce`, and a `lastEmittedRef` re-emit guard. CLAUDE.md >
 * "Frontend API Conventions": no raw `<input>` for entity forms.
 *
 * INVALID NEVER PROPAGATES: on a failed parse the effect below returns
 * without emitting and without touching `lastEmittedRef`. `PaymentStep`
 * feeds the emitted value straight back in as `valueMonths`, so emitting on
 * invalid would wipe the field mid-keystroke — mirrors
 * `DepositInput.tsx:104-119`.
 *
 * `min`/`max` are deliberately NON-OPTIONAL: the render gate — whether the
 * trio resolves at all — lives in `PaymentStep`, so this component never
 * has to represent "no bounds" as a runtime state.
 *
 * Accessibility contract (D-B3): a programmatic label (`Label htmlFor` +
 * `Input id`), an announced, associated error (`role="alert"` +
 * `aria-describedby`), exposed invalidity (`aria-invalid`), no duplicate
 * announcement (the shared `Input`'s own `hint` is suppressed while
 * erroring), a native `type="number"` field (Tab to focus, digits and
 * Up/Down to change — no custom key handling, no `onMouseDown`-only
 * interaction), and bounds discoverable from the hint without reading the
 * error.
 *
 * MOUNT VALIDATION (#779). `mode: 'onChange'` governs event-driven
 * revalidation only — react-hook-form never runs the resolver against a
 * value supplied via `values:` on first render, because nothing counts as an
 * "event" at mount. A persisted `valueMonths` that is already outside
 * `[min, max]` (the product's bounds narrowed after the quote was saved)
 * therefore rendered with `aria-invalid="false"` and no `role="alert"` —
 * looking valid right up to the 422 the server returns on save. The fix
 * below is `useEffect(() => { void trigger(); }, [trigger, schema])`: it
 * runs the SAME Zod schema the field already uses, so it is incapable of
 * producing an error for a blank field or an in-bounds value (both are valid
 * inputs to that schema) — the only values it can flag are exactly the ones
 * the server would reject. Keyed on `schema` (not `[]`) so the value already
 * on screen is re-checked when the product's bounds narrow mid-session, not
 * only when the component first mounts.
 *
 * Two prohibitions, both because they move focus on mount, which the
 * accessibility contract above forbids for an error the agent has not yet
 * interacted with:
 *   - NEVER pass `{ shouldFocus: true }` to `trigger()` — the default is
 *     falsy and must stay that way.
 *   - NEVER use the `errors:` `useForm` prop as an alternative mechanism —
 *     setting it calls `control._focusError()` internally.
 */
export const CollectionLeadInput: React.FC<CollectionLeadInputProps> = ({
    min,
    max,
    productDefault,
    valueMonths,
    onDebouncedChange,
    debounceMs = DEBOUNCE_MS,
}) => {
    const schema = useMemo(() => buildCollectionLeadSchema(min, max), [min, max]);

    const {
        control,
        watch,
        trigger,
        formState: { errors },
    } = useForm<CollectionLeadFormData>({
        values: { collectionLeadMonths: valueMonths },
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    // #779 — validate the value `values:` just hydrated. `mode: 'onChange'`
    // only reacts to a change/blur event; nothing runs the resolver against
    // the value supplied on first render otherwise. `trigger` is
    // referentially stable (react-hook-form builds `_formControl` once), and
    // `schema` is itself `useMemo`'d on `[min, max]` above, so this
    // satisfies `react-hooks/exhaustive-deps` with no suppression. See the
    // docblock above for why this is the only mechanism that flags an
    // out-of-bounds hydrated value without also risking a false positive or
    // moving focus.
    useEffect(() => {
        void trigger();
    }, [trigger, schema]);

    const watchedMonths = watch('collectionLeadMonths');
    const debouncedMonths = useDebounce(watchedMonths, debounceMs);

    // Guards against re-emitting the same value on every render (e.g. a
    // live-preview refetch re-rendering the parent) — only a genuine change
    // in the debounced, validated value should notify the parent.
    const lastEmittedRef = useRef<number | undefined>(valueMonths);

    useEffect(() => {
        // A genuinely empty field (the user cleared it) is the one case that
        // should propagate `undefined` — that clears any previously-emitted
        // override back to the product's default (D-B7).
        if (debouncedMonths === undefined) {
            if (lastEmittedRef.current !== undefined) {
                lastEmittedRef.current = undefined;
                onDebouncedChange(undefined);
            }
            return;
        }

        const parsed = schema.safeParse({ collectionLeadMonths: debouncedMonths });
        if (!parsed.success) {
            // A non-empty value that fails validation (out of bounds, not an
            // integer) is an in-progress invalid edit, NOT a clear. Never
            // emit here — see the docblock above for why.
            return;
        }

        const nextMonths = parsed.data.collectionLeadMonths as number;
        if (nextMonths !== lastEmittedRef.current) {
            lastEmittedRef.current = nextMonths;
            onDebouncedChange(nextMonths);
        }
        // Only the settled, debounced value (and the schema it is judged
        // against) should re-run this effect — `onDebouncedChange`'s
        // identity churn on every parent render must not re-fire it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedMonths, schema]);

    const errorId = 'collectionLeadMonths-error';
    const hasError = !!errors.collectionLeadMonths;

    return (
        <div data-testid="collection-lead-input">
            <Label htmlFor="collectionLeadMonths">Collection lead (months)</Label>
            <Controller
                name="collectionLeadMonths"
                control={control}
                render={({ field }) => (
                    <Input
                        type="number"
                        id="collectionLeadMonths"
                        step={1}
                        min={String(min)}
                        max={String(max)}
                        value={field.value ?? ''}
                        error={hasError}
                        // The shared `Input`'s own `hint` renders a bare,
                        // unassociated <p> — fine for the informational
                        // hint, but a screen-reader user gets nothing for a
                        // validation error. When erroring, the message is
                        // rendered below via our own `role="alert"` element
                        // instead (associated by `aria-describedby`), so the
                        // built-in hint is left empty to avoid a duplicate
                        // announcement.
                        // DELIBERATE DEVIATION from the plan's mandated hint
                        // string ("... Leave blank to use this product's
                        // default of N."), which described a resting state
                        // this field cannot hold. `PaymentStep` controls it
                        // with `effectiveLeadMonths` (D-B7 — the agent sees
                        // the lead actually in force, not a blank box), and
                        // `values:` re-syncs the field the moment it diverges,
                        // so clearing it restores the number SYNCHRONOUSLY, on
                        // the `onChange` itself, before the debounce runs. The
                        // clear still WORKS — `onDebouncedChange(undefined)`
                        // fires and the schedule returns to the product
                        // default — but the blank is never visible, so the old
                        // copy promised something the user could not observe.
                        // Do NOT "restore" it from the plan: the pre-fill is
                        // the decision, and this copy is what makes the two
                        // agree. Both bounds and the product default stay
                        // named, per D-B3's discoverability requirement.
                        hint={
                            hasError
                                ? undefined
                                : `Between ${min} and ${max} months. This product's default is ${productDefault}.`
                        }
                        aria-invalid={hasError}
                        aria-describedby={hasError ? errorId : undefined}
                        onChange={(event) => {
                            const raw = event.target.value;
                            field.onChange(raw === '' ? undefined : Number(raw));
                        }}
                        onBlur={field.onBlur}
                    />
                )}
            />
            {hasError && (
                <p id={errorId} role="alert" className="mt-1.5 text-xs text-mp-error">
                    {errors.collectionLeadMonths?.message}
                </p>
            )}
        </div>
    );
};
